import { useEffect, useState } from "react";

import useSettings from "../../data/useSettings";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ISetting, SettingInputType, SettingType } from "../../models/Setting";
import AdminSettingsTable from "../../components/adminSettings/AdminSettingsTable";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";

// The two settings behind "Generate code" on the Waste Tracking page and the yard app. Kept
// apart from the site details so they can be created from here rather than by support.
const PREMISES_CODE_KEY = "DEFRA:ConsignmentPremisesCode";
const NEXT_NUMBER_KEY = "DEFRA:ConsignmentNoteNextNumber";
const NUMBERING_KEYS = [PREMISES_CODE_KEY, NEXT_NUMBER_KEY];

// Site details DEFRA needs on every Receipt of Waste. Same DEFRA:* settings as Admin Settings,
// filtered into one place - mirrors CodSettings for the COD: keys.
export default function WasteTrackingSettings() {
  const { setInfo } = usePageNameContext();
  const { generalSettings, loading, create } = useSettings();
  const { saveHandlers } = useDefaultCRUDHandlers("Consignment note numbering");
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    setInfo({
      name: "Waste Tracking",
      desc: "DEFRA receiver details used on every waste movement",
    });
  }, []);

  const allDefra = (generalSettings ?? []).filter((s) => s.key.startsWith("DEFRA:"));
  const defraSettings = allDefra.filter((s) => !NUMBERING_KEYS.includes(s.key));
  const numberingSettings = allDefra.filter((s) => NUMBERING_KEYS.includes(s.key));
  const missingNumbering = NUMBERING_KEYS.filter((key) => !numberingSettings.some((s) => s.key === key));

  // Creates whichever of the two keys this customer doesn't have yet. Values are blank / 1 so the
  // API refuses to generate until the premises code has actually been typed in below.
  const setUpNumbering = async () => {
    setSettingUp(true);
    try {
      for (const key of missingNumbering) {
        const setting = {
          key,
          value: key === NEXT_NUMBER_KEY ? "1" : "",
          settingType: SettingType.General,
          inputType: key === NEXT_NUMBER_KEY ? SettingInputType.Number : SettingInputType.Text,
          required: false,
        } as ISetting;
        await create.mutateAsync(setting);
      }
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    } finally {
      setSettingUp(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">DEFRA Site Details</h2>
        <p className="mt-1 text-sm text-gray-500">
          Sent as the receiver on every movement. All of <span className="font-mono">ApiCode</span>,{" "}
          <span className="font-mono">SiteName</span>, <span className="font-mono">AuthorisationNumber</span> (permit),{" "}
          <span className="font-mono">SiteAddress</span> and <span className="font-mono">SitePostcode</span> must be filled in
          before anything can be submitted. <span className="font-mono">CarrierRegistrationNumber</span> is your own carrier
          registration for vehicles your drivers collect; <span className="font-mono">HazardCodes</span> (comma separated HP codes)
          and <span className="font-mono">RecoveryCode</span> are optional overrides (RecoveryCode defaults to R13, storage pending recovery, the code for a vehicle on receipt); <span className="font-mono">TrackFrom</span>{" "}
          hides vehicles collected before that date from the pending list.
        </p>

        {loading ? (
          <div className="mt-4">
            <LoadingWheel />
          </div>
        ) : defraSettings.length ? (
          <AdminSettingsTable settings={defraSettings} />
        ) : (
          <p className="mt-4 text-sm text-gray-500">No DEFRA settings found for this customer yet - please contact support to have them set up.</p>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Consignment Note Numbering</h2>
        <p className="mt-1 text-sm text-gray-500">
          Drives the <span className="font-semibold">Generate code</span> button on the Waste Tracking page and the yard app's
          check-in screen, for notes that arrive without a pre-printed code. Codes are issued as{" "}
          <span className="font-mono">PREMISES/A0001</span>, <span className="font-mono">A0002</span> … then{" "}
          <span className="font-mono">B0001</span>. <span className="font-mono">ConsignmentPremisesCode</span> is your premises
          code: under Environment Agency rules the first six letters or digits of your business name (two to six characters).{" "}
          <span className="font-mono">ConsignmentNoteNextNumber</span> is the lowest number the next generated code may use - Wilma
          moves it on automatically after each code, and never re-issues a number already on another vehicle, so you only
          need to change it to start from a particular point.
        </p>

        {loading ? (
          <div className="mt-4">
            <LoadingWheel />
          </div>
        ) : (
          <>
            {numberingSettings.length > 0 && <AdminSettingsTable settings={numberingSettings} />}
            {missingNumbering.length > 0 && (
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  {numberingSettings.length === 0
                    ? "Numbering has not been set up for this customer yet."
                    : `Missing: ${missingNumbering.map((k) => k.replace("DEFRA:", "")).join(", ")}.`}
                </p>
                <ButtonInput
                  label={settingUp ? "Setting up…" : "Set up numbering"}
                  isSubmit={false}
                  disabled={settingUp}
                  onClick={setUpNumbering}
                  classes={undefined}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
